import { useCallback, useRef } from 'react';

import { TileCarousel } from '#/components/TileCarousel/TileCarousel';
import { useIsUsingAlgoliaMock } from '#/config/AppConfigContext';
import { useFetchFn } from '#/hooks/useFetchFn';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SalariesSubmittedData } from '#/modules/salaries/types/SalariesSubmittedData';
import { searchAlgoliaFetchSimilarJobs } from '#/modules/search/algolia/fetch/similarJobs/searchAlgoliaFetchSimilarJobs';
import { useSearchLocationOrEmpty } from '#/modules/search/contexts/SearchContext';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetJobsRoutePath } from '#/modules/search/routing/helpers/searchGetJobsRoutePath';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';

import { RelatedJobsContainer, RelatedJobsWrapper } from './RelatedJobs.styled';

type StandardCmsRelatedJobsProps = {
  headline: string;
  submittedData?: SalariesSubmittedData;
};

export function RelatedJobs({
  submittedData = {},
  headline,
}: StandardCmsRelatedJobsProps) {
  const { isUsingAlgoliaMock } = useIsUsingAlgoliaMock();
  const { i18n, locale } = useI18n();

  const {
    selectedSynonymFamilyId,
    selectedSynonymId,
    selectedSynonymLevel,
    jobTitleId,
    jobTitleSelected,
    jobTitleStandard,
    city,
    geoType,
    latitude,
    longitude,
    stateCode,
  } = submittedData;
  const workLocation =
    city && stateCode
      ? {
          geoType,
          latitude: latitude ? parseFloat(latitude.toFixed(4)) : undefined,
          longitude: longitude ? parseFloat(longitude.toFixed(4)) : undefined,
          text: `${city}, ${stateCode}`,
        }
      : undefined;
  const relatedJobsRef = useRef<HTMLDivElement>(null);

  const searchLocation = useSearchLocationOrEmpty();

  const synonym = {
    familyId: selectedSynonymFamilyId ?? '',
    id: selectedSynonymId ?? '',
    level: selectedSynonymLevel ?? 0,
    jobTitleId: jobTitleId ?? '',
    name: jobTitleSelected ?? '',
  };

  const [similarJobsResponse] = useFetchFn({
    fn: searchAlgoliaFetchSimilarJobs,
    args: [
      jobTitleStandard ?? '',
      synonym,
      [],
      workLocation ?? searchLocation,
      [],
      isUsingAlgoliaMock,
    ],
  });

  useElementReachedEffect({
    ref: relatedJobsRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Displayed: Featured Listings', {
        context: 'Search Promo',
        carousel_location: 'Salary Report',
        listing_type: 'JOB',
        listing_count: similarJobsResponse?.hits.length,
        layout: 'REGULAR',
        results_type: similarJobsResponse?.resultsType,
        query_id: similarJobsResponse?.queryId,
      });
    }, [similarJobsResponse]),
  });

  if (
    !similarJobsResponse ||
    (Array.isArray(similarJobsResponse) && similarJobsResponse.length === 0)
  )
    return null;

  type AdditionalAnalytics = {
    job_family_id?: string;
  };

  const additionalAnalyticsProps = {} as AdditionalAnalytics;

  if (selectedSynonymFamilyId)
    additionalAnalyticsProps.job_family_id = selectedSynonymFamilyId;

  return (
    <RelatedJobsContainer ref={relatedJobsRef}>
      <RelatedJobsWrapper data-qa-id="recommended-jobs-section">
        <TileCarousel
          headline={headline}
          results={similarJobsResponse?.hits}
          seeAll={{
            /* TRANSLATORS: Link to job search */
            text: i18n('See all jobs'),
            action: {
              type: 'link',
              url: searchGetJobsRoutePath({ locale }),
              state: {
                searchLocation: workLocation ?? searchLocation,
              } satisfies SearchPageLocationState,
            },
          }}
          listingType="JOB"
          carouselLocation="Salary Report"
          additionalAnalyticsProps={additionalAnalyticsProps}
        />
      </RelatedJobsWrapper>
    </RelatedJobsContainer>
  );
}
